import { useEffect, useState } from "react";
import axios from "axios";
import { Play, ExternalLink, Activity, Receipt, CheckCircle2 } from "lucide-react";
import { API } from "@/App";

/**
 * IMS HART banner with live counters pulled from /api/hospital/hart/stats.
 * Shows three inline pills: tracked / pending billing / billed this month.
 * Falls back to a clean "Watch Demo" CTA if the hospital has no HART data yet.
 */
export default function ImsHartBanner() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("hospital_token") : null;
    if (!token) return;
    axios
      .get(`${API}/hospital/hart/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStats(r.data))
      .catch(() => setStats({ has_data: false, total_tracked: 0, pending_billing: 0, billed_this_month: 0 }));
  }, []);

  const hasData = stats && stats.has_data;

  return (
    <a
      href="https://www.youtube.com/watch?v=Ng1roAYbGJc"
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 rounded-xl p-5 text-white hover:from-indigo-700 hover:via-purple-700 hover:to-indigo-800 transition-all duration-300 shadow-lg hover:shadow-xl group"
      data-testid="ims-hart-banner"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Play className="w-7 h-7 text-white fill-current" />
          </div>
          <div className="min-w-0">
            <p className="text-indigo-200 text-sm font-medium">
              {hasData ? "LIVE • IMS HART" : "NEW • Introducing"}
            </p>
            <h3 className="text-xl font-bold">IMS HART — Implant Management System</h3>
            <p className="text-indigo-200 text-sm mt-1">
              {hasData
                ? "Real-time implant tracking & revenue capture for your OR."
                : "Watch the demo video to see how HART transforms implant tracking & billing."}
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg group-hover:bg-white/20 transition-colors flex-shrink-0">
          <span className="font-medium">Watch Demo</span>
          <ExternalLink className="w-4 h-4" />
        </div>
      </div>

      {hasData && (
        <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-3 gap-3" data-testid="ims-hart-stats">
          <Stat icon={Activity} value={stats.total_tracked} label="Implants Tracked" testid="hart-stat-tracked" />
          <Stat icon={Receipt} value={stats.pending_billing} label="Pending Billing" testid="hart-stat-pending" highlight={stats.pending_billing > 0} />
          <Stat icon={CheckCircle2} value={stats.billed_this_month} label="Billed This Month" testid="hart-stat-billed" />
        </div>
      )}
    </a>
  );
}

function Stat({ icon: Icon, value, label, testid, highlight }) {
  return (
    <div className="flex items-center gap-2.5" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${highlight ? "bg-amber-400/30" : "bg-white/15"}`}>
        <Icon className={`w-4 h-4 ${highlight ? "text-amber-200" : "text-white"}`} />
      </div>
      <div className="min-w-0">
        <div className={`text-xl font-extrabold leading-none ${highlight ? "text-amber-200" : "text-white"}`}>{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-indigo-200 mt-0.5 truncate">{label}</div>
      </div>
    </div>
  );
}
